import React, { useState, useEffect } from "react";
import { Dropdown, Button, Modal } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import { Link } from "react-router-dom";
import { isEmpty } from "ramda";

import { deleteUser } from "../../utils/user_utils";
import { toggleUserReceiveEmailAlert } from "../../services/apis";
import { hasError, isServerResponseError } from "../../utils/common";

const UsersListTable = ({
  id = "table-list",
  headers = [],
  items = [],
  refreshData,
  canEdit,
  canDelete,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [alertMap, setAlertMap] = useState({});
  const [togglingId, setTogglingId] = useState(null);
  const { addToast } = useToasts();

  useEffect(() => {
    setAlertMap(
      items.reduce(
        (acc, item) => ({ ...acc, [item.id]: !!item.is_alert }),
        {}
      )
    );
  }, [items]);

  const alertToggleHandler = async (user) => {
    const nextValue = !alertMap[user.id];
    setTogglingId(user.id);
    // optimistic update
    setAlertMap((prev) => ({ ...prev, [user.id]: nextValue }));

    const response = await toggleUserReceiveEmailAlert({
      email: user.email,
      is_alert: nextValue,
    });

    if (isServerResponseError(response) || hasError(response)) {
      // revert on failure
      setAlertMap((prev) => ({ ...prev, [user.id]: !nextValue }));
      addToast("Update failed. You can re-try later.", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 1500,
      });
    } else {
      addToast(
        nextValue
          ? "Added to alert email list."
          : "Removed from alert email list.",
        {
          appearance: "success",
          autoDismiss: true,
          autoDismissTimeout: 1500,
        }
      );
    }
    setTogglingId(null);
  };

  const userDeleteClickHandler = (event) => {
    setSelectedUserId(event.target.id);
    setShowConfirmDialog(true);
  };
  const deleteNoHandler = () => setShowConfirmDialog(false);
  const deleteYesHandler = async () => {
    const response = await deleteUser(selectedUserId, {});
    if (response === 0) {
      addToast("Delete failed. You can re-try later.", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 1500,
      });
    } else if (response === 1) {
      addToast("Delete user successfully.", {
        appearance: "success",
        autoDismiss: true,
        autoDismissTimeout: 1500,
      });
      refreshData && refreshData();
    }
    setShowConfirmDialog(false);
  };

  return (
    <>
      <div className="table-responsive">
        <table className="table" id={id}>
          <thead>
            <tr>
              {isEmpty(headers)
                ? null
                : headers.map((header, index) => (
                    <th
                      key={index + ""}
                      scope="col"
                      className={
                        header === "Receive alert email" ? "text-center" : ""
                      }
                    >
                      {header}
                    </th>
                  ))}
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index + ""}>
                <td>{item.name}</td>
                <td>{item.email}</td>
                <td>{item.role_name}</td>
                <td>{item.created_at}</td>
                <td className="text-center align-middle">
                  <input
                    type="checkbox"
                    id={`receive-alert-${item.id}`}
                    checked={!!alertMap[item.id]}
                    disabled={togglingId === item.id}
                    onChange={() => alertToggleHandler(item)}
                    style={{
                      width: "20px",
                      height: "20px",
                      cursor: togglingId === item.id ? "not-allowed" : "pointer",
                    }}
                  />
                </td>
                <td className="text-right">
                  <Dropdown>
                    <Dropdown.Toggle
                      variant=""
                      className="btn-text dropdown-toggle"
                    >
                      <i className="far fa-ellipsis-v" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="right">
                      {canEdit && (
                        <Dropdown.Item as={Link} to={`/user/${item.id}/edit`}>
                          Edit
                        </Dropdown.Item>
                      )}
                      {canDelete && (
                        <Dropdown.Item
                          as={Button}
                          id={item.id}
                          onClick={userDeleteClickHandler}
                        >
                          Delete
                        </Dropdown.Item>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showConfirmDialog && (
        <Modal
          centered={true}
          show={showConfirmDialog}
          onHide={deleteNoHandler}
        >
          <Modal.Header closeButton className="modal-center">
            <Modal.Title>Do you want to delete this user</Modal.Title>
          </Modal.Header>
          {/*<Modal.Body>User id {selectedUserId}</Modal.Body>*/}
          <Modal.Footer className="modal-center">
            <Button variant="secondary" onClick={deleteNoHandler}>
              No
            </Button>
            <Button variant="primary" onClick={deleteYesHandler}>
              Yes
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default UsersListTable;
